import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import {
  useGetSystemGroupsQuery,
  useGetScheduledNotificationsQuery,
  useScheduleNotificationMutation,
  useRescheduleNotificationMutation,
  useCancelScheduledNotificationMutation,
} from '../../api/groupApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import MessageField from '../../components/MessageField/MessageField';
import { messageMaxLengthForChannels } from '../../utils/messageLimits';
import s from '../../styles/shared/page.module.css';
import buttons from '../../styles/shared/buttons.module.css';
import styles from './ScheduledNotificationsPage.module.css';

const SYSTEM_ORG_ID = '00000000-0000-0000-0000-000000000001';

const STATUS_COLOR = {
  pending: 'var(--color-accent)',
  sent: 'var(--color-success)',
};

function formatDateTime(date) {
  return `${date.toLocaleDateString()} ${formatTime12h(date.getHours() * 60 + date.getMinutes())}`;
}

function toTimeString(date) {
  if (!date) return null;
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function applyTimeString(date, timeStr) {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const next = new Date(date);
  next.setHours(hours, minutes, 0, 0);
  return next;
}

function formatTime12h(totalMinutes) {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

const TIME_STEP_MINUTES = 15;
const TIME_OPTIONS = Array.from({ length: (24 * 60) / TIME_STEP_MINUTES }, (_, i) => {
  const totalMinutes = i * TIME_STEP_MINUTES;
  const value = `${String(Math.floor(totalMinutes / 60)).padStart(2, '0')}:${String(totalMinutes % 60).padStart(2, '0')}`;
  return { label: formatTime12h(totalMinutes), value };
});

const SLOT_HEIGHT_PX = 52;
const PIXELS_PER_MINUTE = SLOT_HEIGHT_PX / TIME_STEP_MINUTES;

const ALL_GROUPS = '__all_groups__';

export default function ScheduledNotificationsPage() {
  const location = useLocation();
  const { data: groups, isLoading: loadingGroups } = useGetSystemGroupsQuery();
  const [groupId, setGroupId] = useState(location.state?.groupId ?? ALL_GROUPS);
  const channelsByGroupId = useMemo(
    () => new Map((groups ?? []).map((g) => [g.id, g.channels])),
    [groups],
  );

  const { data: schedule, isError, error } = useGetScheduledNotificationsQuery(SYSTEM_ORG_ID);
  const [scheduleNotification, { isLoading: isScheduling }] = useScheduleNotificationMutation();
  const [rescheduleNotification, { isLoading: isRescheduling }] = useRescheduleNotificationMutation();
  const [cancelScheduledNotification, { isLoading: isDeleting }] = useCancelScheduledNotificationMutation();
  const showToast = useToast();

  const [selected, setSelected] = useState(null);
  const [editTime, setEditTime] = useState(null);
  const [editMessage, setEditMessage] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [pendingCreate, setPendingCreate] = useState(null);
  const [pendingMessage, setPendingMessage] = useState('');

  const tooltipRef = useRef(null);
  const calendarWrapperRef = useRef(null);
  const nowLineRef = useRef(null);

  function updateNowLine() {
    const wrapper = calendarWrapperRef.current;
    if (!wrapper) return;
    const body = wrapper.querySelector('.fc-timegrid-body');
    if (!body) return;

    let line = nowLineRef.current;
    if (!line || !body.contains(line)) {
      line = document.createElement('div');
      line.className = styles.fullWidthNowLine;
      body.appendChild(line);
      nowLineRef.current = line;
    }

    const nativeLine = wrapper.querySelector('.fc-timegrid-now-indicator-line');
    if (nativeLine) {
      const top = nativeLine.getBoundingClientRect().top - body.getBoundingClientRect().top;
      line.style.top = `${top}px`;
      return;
    }

    const now = new Date();
    const minutesSinceMidnight = now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
    line.style.top = `${minutesSinceMidnight * PIXELS_PER_MINUTE}px`;
  }

  const filteredSchedule = useMemo(
    () =>
      (schedule ?? []).filter((item) => {
        if (groupId !== ALL_GROUPS && item.group_id !== groupId) return false;
        return true;
      }),
    [schedule, groupId],
  );

  const events = useMemo(
    () =>
      filteredSchedule.map((item) => ({
        id: item.id,
        title: item.message,
        start: item.scheduled_at,
        backgroundColor: STATUS_COLOR[item.status],
        borderColor: STATUS_COLOR[item.status],
        startEditable: item.status === 'pending',
        durationEditable: false,
      })),
    [filteredSchedule],
  );

  useEffect(() => {
    const timeout = window.setTimeout(updateNowLine, 100);
    const interval = window.setInterval(updateNowLine, 30000);
    return () => {
      window.clearTimeout(timeout);
      window.clearInterval(interval);
    };
  }, [events]);

  function handleEventClick(info) {
    const item = (schedule ?? []).find((it) => it.id === info.event.id);
    if (!item) return;
    setSelected(item);
    setEditTime(toTimeString(new Date(item.scheduled_at)));
    setEditMessage(item.message);
    setConfirmingDelete(false);
  }

  function closeSelected() {
    setSelected(null);
    setConfirmingDelete(false);
  }

  function handleSelect(info, calendarApi) {
    calendarApi.unselect();
    if (groupId === ALL_GROUPS) {
      showToast('error', 'Schedule', 'Pick a specific group above first.');
      return;
    }
    setPendingCreate(info.start);
    setPendingMessage('');
  }

  async function handleEventDrop(info) {
    try {
      await rescheduleNotification({ id: info.event.id, scheduledAt: info.event.start.toISOString() }).unwrap();
      showToast('success', 'Schedule', `Notification moved to ${formatDateTime(info.event.start)}.`);
    } catch (err) {
      showToast('error', 'Schedule', errMsg(err, 'Could not move the notification.'));
      info.revert();
    }
  }

  async function handleConfirmCreate() {
    if (!pendingCreate || !pendingMessage.trim()) return;
    try {
      await scheduleNotification({
        groupId,
        message: pendingMessage.trim(),
        scheduledAt: pendingCreate.toISOString(),
      }).unwrap();
      showToast('success', 'Schedule', `Notification scheduled for ${formatDateTime(pendingCreate)}.`);
      setPendingCreate(null);
      setPendingMessage('');
    } catch (err) {
      showToast('error', 'Schedule', errMsg(err, 'Could not schedule notification.'));
    }
  }

  async function handleSave() {
    if (!selected || !editTime || !editMessage.trim()) return;
    const next = applyTimeString(new Date(selected.scheduled_at), editTime);
    try {
      await rescheduleNotification({
        id: selected.id,
        scheduledAt: next.toISOString(),
        message: editMessage.trim(),
      }).unwrap();
      showToast('success', 'Schedule', `Notification updated — now at ${formatDateTime(next)}.`);
      closeSelected();
    } catch (err) {
      showToast('error', 'Schedule', errMsg(err, 'Could not update the notification.'));
    }
  }

  async function handleDelete() {
    try {
      await cancelScheduledNotification(selected.id).unwrap();
      showToast('success', 'Schedule', 'Scheduled notification canceled.');
      closeSelected();
    } catch (err) {
      showToast('error', 'Schedule', errMsg(err, 'Could not cancel the scheduled notification.'));
    }
  }

  function handleCalendarMouseMove(e) {
    const cell = e.target.closest('td.fc-timegrid-slot-lane');
    if (!cell || !tooltipRef.current) {
      if (tooltipRef.current) tooltipRef.current.style.display = 'none';
      return;
    }
    const [hours, minutes] = cell.getAttribute('data-time').split(':');
    tooltipRef.current.textContent = formatTime12h(Number(hours) * 60 + Number(minutes));
    tooltipRef.current.style.display = 'block';
    tooltipRef.current.style.left = `${e.clientX + 14}px`;
    tooltipRef.current.style.top = `${e.clientY + 14}px`;
  }

  function handleCalendarMouseLeave() {
    if (tooltipRef.current) tooltipRef.current.style.display = 'none';
  }

  if (isError) {
    return (
      <div className={s.page}>
        <h1>Schedule</h1>
        <p className="textMuted">{errMsg(error, 'Could not load the schedule.')}</p>
      </div>
    );
  }

  if (loadingGroups) return <p className="textMuted">Loading…</p>;
  if (!groups?.length)
    return (
      <div className={s.page}>
        <h1>Schedule</h1>
        <p className={`${s.subtitle} textMuted`}>No system groups yet — add one first.</p>
      </div>
    );

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1>Schedule</h1>
          <p className={`${s.subtitle} textMuted`}>
            Pick a group, then click a time slot to queue a notification. Drag an existing one to
            move it.
          </p>
        </div>
      </div>

      <div className={styles.pickers}>
        <div className="field">
          <label htmlFor="group">Group</label>
          <Dropdown
            inputId="group"
            value={groupId}
            onChange={(e) => setGroupId(e.value)}
            options={[
              { label: 'All groups', value: ALL_GROUPS },
              ...groups.filter((g) => g.enabled).map((g) => ({ label: g.name, value: g.id })),
            ]}
            className="w-full"
          />
        </div>
      </div>

      <div
        ref={calendarWrapperRef}
        className={styles.calendarWrapper}
        onMouseMove={handleCalendarMouseMove}
        onMouseLeave={handleCalendarMouseLeave}
      >
        <FullCalendar
          plugins={[timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{ left: 'prev,next today', center: 'title', right: 'timeGridWeek,timeGridDay' }}
          slotDuration="00:15:00"
          slotLabelInterval="01:00:00"
          defaultTimedEventDuration="00:15:00"
          allDaySlot={false}
          nowIndicator
          datesSet={updateNowLine}
          selectable
          select={(info) => handleSelect(info, info.view.calendar)}
          events={events}
          eventClick={handleEventClick}
          editable
          eventDurationEditable={false}
          eventDrop={handleEventDrop}
          height={700}
          scrollTime="07:00:00"
        />
        <div ref={tooltipRef} className={styles.timeTooltip} style={{ display: 'none' }} />
      </div>

      <Dialog
        header="Schedule notification?"
        visible={Boolean(pendingCreate)}
        onHide={() => setPendingCreate(null)}
        style={{ width: '420px' }}
        modal
      >
        <p className={styles.detailRow}>
          For <strong>{pendingCreate ? formatDateTime(pendingCreate) : ''}</strong>:
        </p>
        <MessageField
          id="pendingMessage"
          value={pendingMessage}
          onChange={setPendingMessage}
          rows={5}
          autoFocus
          maxLength={messageMaxLengthForChannels(channelsByGroupId.get(groupId))}
        />
        <div className={styles.dialogActionsRow}>
          <Button
            type="button"
            label="Cancel"
            className={`${buttons.saveBtn} ${styles.dialogBtn} ${styles.neutralBtn}`}
            onClick={() => setPendingCreate(null)}
            disabled={isScheduling}
          />
          <Button
            type="button"
            label="Add"
            className={`${buttons.saveBtn} ${styles.dialogBtn}`}
            onClick={handleConfirmCreate}
            loading={isScheduling}
            disabled={!pendingMessage.trim()}
          />
        </div>
      </Dialog>

      <Dialog
        header="Scheduled notification"
        visible={Boolean(selected)}
        onHide={closeSelected}
        style={{ width: '420px' }}
        modal
      >
        {selected && selected.status !== 'pending' && (
          <p className={styles.detailRow} style={{ whiteSpace: 'pre-wrap' }}>
            {selected.message}
          </p>
        )}
        {selected && selected.status === 'pending' && !confirmingDelete && (
          <>
            <MessageField
              id="editMessage"
              value={editMessage}
              onChange={setEditMessage}
              rows={5}
              maxLength={messageMaxLengthForChannels(channelsByGroupId.get(selected.group_id))}
            />
            <div className="field">
              <label htmlFor="editTime">Time on {new Date(selected.scheduled_at).toLocaleDateString()}</label>
              <Dropdown
                inputId="editTime"
                value={editTime}
                onChange={(e) => setEditTime(e.value)}
                options={TIME_OPTIONS}
                className="w-full"
              />
            </div>
            <div className={styles.dialogActionsRow}>
              <Button
                type="button"
                label="Cancel notification"
                className={`${buttons.dangerBtn} ${styles.dialogBtn}`}
                onClick={() => setConfirmingDelete(true)}
              />
              <Button
                type="button"
                label="Save"
                className={`${buttons.saveBtn} ${styles.dialogBtn}`}
                onClick={handleSave}
                loading={isRescheduling}
                disabled={!editMessage.trim()}
              />
            </div>
          </>
        )}
        {selected && selected.status === 'pending' && confirmingDelete && (
          <>
            <p className={styles.detailRow} style={{ whiteSpace: 'pre-wrap' }}>
              {selected.message}
            </p>
            <p className={styles.detailRow}>Cancel this scheduled notification? This can&apos;t be undone.</p>
            <div className={styles.dialogActionsRow}>
              <Button
                type="button"
                label="No, keep it"
                className={`${buttons.saveBtn} ${styles.dialogBtn} ${styles.neutralBtn}`}
                onClick={() => setConfirmingDelete(false)}
                disabled={isDeleting}
              />
              <Button
                type="button"
                label="Yes, cancel it"
                className={`${buttons.dangerBtn} ${styles.dialogBtn}`}
                onClick={handleDelete}
                loading={isDeleting}
              />
            </div>
          </>
        )}
        {selected && selected.status !== 'pending' && (
          <>
            <p className={styles.detailRow}>
              <strong>When:</strong> {formatDateTime(new Date(selected.scheduled_at))}
            </p>
            <p className={styles.detailRow}>
              <strong>Status:</strong> {selected.status}
            </p>
          </>
        )}
      </Dialog>
    </div>
  );
}
